import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TeacherHomeworkList from './TeacherHomeworkList';
import TeacherHomeworkBoard from './TeacherHomeworkBoard';
import TeacherQuickHomework from './TeacherQuickHomework';
import { normalizeWhatsappText } from '../../utils/richText';

const normalizeHomework = homework => ({ ...homework, description: normalizeWhatsappText(homework.description || '') });

export default function TeacherHomeworkFlow({ onRequest, classCode, onBack }) {
  const [quick, setQuick] = useState(false);
  const [success, setSuccess] = useState('');
  const [bucket, setBucket] = useState('active');
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState({});
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({});
  const [bulkPending, setBulkPending] = useState(false);
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const alive = useRef(false);
  const locks = useRef(new Set());

  const navigate = id => {
    // Clear the previous DTO before rendering the next view, and ignore late writes immediately.
    generation.current += 1;
    setData(null); setLoading(true); setError(''); setSelected(id);
  };

  const request = useCallback(async (path, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try { return await onRequest(path, { ...options, signal: controller.signal }); }
    finally { clearTimeout(timeout); }
  }, [onRequest]);

  useEffect(() => { alive.current = true; return () => { alive.current = false; generation.current += 1; }; }, []);
  useEffect(() => {
    const current = ++generation.current;
    setLoading(true); setError(''); setData(null); setPending({}); setErrors({}); setFeedback({}); setBulkPending(false); setNotice('');
    request(selected == null ? `homeworks?bucket=${bucket}` : `homeworks/${encodeURIComponent(selected)}`)
      .then(payload => {
        if (alive.current && generation.current === current) setData(selected == null
          ? { ...payload, items: payload.items.map(normalizeHomework) }
          : { ...payload, homework: normalizeHomework(payload.homework) });
      }).catch(err => {
        if (alive.current && generation.current === current && err.status !== 401) setError('Ödevler yüklenemedi. Yeniden deneyin.');
      }).finally(() => { if (alive.current && generation.current === current) setLoading(false); });
    // Leaving never awaits writes; a later visit always reads the server again.
    return () => { generation.current += 1; };
  }, [selected, bucket, request, reload]);

  const changeStatus = async (student, status) => {
    const current = generation.current;
    const key = `${current}:${student.student_id}`;
    if (student.status === status || locks.current.has(key)) return;
    locks.current.add(key);
    setPending(value => ({ ...value, [student.student_id]: true }));
    setErrors(value => ({ ...value, [student.student_id]: '' }));
    setFeedback(value => ({ ...value, [student.student_id]: null }));
    try {
      const result = await request(`homeworks/${encodeURIComponent(selected)}/students/${encodeURIComponent(student.student_id)}`,
        { method: 'PATCH', body: JSON.stringify({ status }) });
      if (String(result.record?.student_id) !== String(student.student_id)
        || ![null, 'done', 'missing', 'not_done'].includes(result.record.status)) throw new Error('Invalid status response');
      if (!alive.current || generation.current !== current) return;
      setData(value => ({ ...value, students: value.students.map(item => item.student_id === student.student_id
        ? { ...item, status: result.record.status } : item) }));
      if (result.record.status === 'done') setFeedback(value => ({ ...value, [student.student_id]: {
        id: Date.now(), label: result.awarded_points === 2 ? '⭐ +2' : '✨ Tamam!',
      } }));
    } catch (err) {
      if (alive.current && generation.current === current && err.status !== 401) {
        setErrors(value => ({ ...value, [student.student_id]: 'Kayıt doğrulanamadı. Yenileyerek gerçek durumu kontrol edin.' }));
      }
    } finally {
      locks.current.delete(key);
      if (alive.current && generation.current === current) setPending(value => { const next = { ...value }; delete next[student.student_id]; return next; });
    }
  };

  const markUnrecorded = async () => {
    const current = generation.current;
    const key = `${current}:bulk`;
    if (bulkPending || Object.keys(pending).length || locks.current.has(key)) return;
    locks.current.add(key); setBulkPending(true); setNotice('');
    try {
      const result = await request(`homeworks/${encodeURIComponent(selected)}/mark-unrecorded-done`, { method: 'POST', body: '{}' });
      if (!Array.isArray(result.records) || !Number.isInteger(result.updated) || result.updated !== result.records.length
        || !Number.isInteger(result.skipped_absent) || !Number.isInteger(result.skipped_existing)
        || result.records.some(record => record.status !== 'done' || record.student_id == null)) throw new Error('Invalid bulk response');
      if (!alive.current || generation.current !== current) return;
      const records = new Map(result.records.map(record => [String(record.student_id), record.status]));
      setData(value => ({ ...value, students: value.students.map(student => records.has(String(student.student_id))
        ? { ...student, status: records.get(String(student.student_id)) } : student) }));
      setNotice(`${result.updated} öğrenci Tam yapıldı. ${result.skipped_absent} devamsız, ${result.skipped_existing} değerlendirilmiş öğrenci atlandı.`);
    } catch (err) {
      if (alive.current && generation.current === current && err.status !== 401) setNotice('Toplu kayıt doğrulanamadı. Yenileyerek gerçek durumları kontrol edin.');
    } finally {
      locks.current.delete(key);
      if (alive.current && generation.current === current) setBulkPending(false);
    }
  };

  const refresh = <Button onClick={() => setReload(value => value + 1)} disabled={bulkPending || Object.keys(pending).length > 0}>Yenile</Button>;
  if (quick) return <TeacherQuickHomework onRequest={request} classCode={classCode} onBack={() => setQuick(false)}
    onCompleted={result => {
      setSuccess(`Değerlendirme kaydedildi! ${result.evaluated} öğrenci değerlendirildi. Veli bildirimleri işleme alındı.`);
      setQuick(false); setBucket('active'); navigate(null); setReload(value => value + 1);
    }} />;
  if (selected == null) return <>
    {success && <Alert severity="success" role="status" sx={{ maxWidth: 1600, mx: 'auto', mt: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
    <TeacherHomeworkList classCode={data?.class?.code || classCode} today={data?.today} bucket={bucket}
      homeworks={data?.items || []} loading={loading} error={error} onBucketChange={setBucket}
      onBack={onBack} onOpenHomework={navigate} onQuick={() => { setSuccess(''); setQuick(true); }} />
    {error && <Box sx={{ textAlign: 'center' }}>{refresh}</Box>}
  </>;
  return <Box className="teacher-homework-board-shell">
    <Button className="teacher-homework-board-back" onClick={() => navigate(null)}>← Ödev Listesine Dön</Button>
    {loading ? <Box role="status" sx={{ p: 4 }}><CircularProgress size={28} /> Öğrenciler yükleniyor…</Box>
      : error ? <Alert severity="error">{error}{refresh}</Alert>
        : data && <TeacherHomeworkBoard classCode={data.class.code} homework={data.homework} students={data.students}
          pending={pending} errors={errors} feedback={feedback} bulkPending={bulkPending} notice={notice}
          onStatus={changeStatus} onBulk={markUnrecorded} refresh={refresh} />}
  </Box>;
}
